import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

const ProgressAnalytics = ({ userProgress }) => {
  // Sample data for charts
  const performanceData = [
    { date: '2024-12-01', mcqScore: 65, mockScore: 7.2, readiness: 60 },
    { date: '2024-12-08', mcqScore: 72, mockScore: 7.8, readiness: 68 },
    { date: '2024-12-15', mcqScore: 78, mockScore: 8.1, readiness: 75 },
    { date: '2024-12-22', mcqScore: 81, mockScore: 8.3, readiness: 79 },
    { date: '2024-12-29', mcqScore: 84, mockScore: 8.5, readiness: 82 },
    { date: '2025-01-05', mcqScore: 87, mockScore: 8.7, readiness: 85 }
  ];

  const categoryScores = [
    { category: 'Testing Fundamentals', score: 92, total: 100 },
    { category: 'Automation Frameworks', score: 85, total: 100 },
    { category: 'Performance Testing', score: 73, total: 100 },
    { category: 'Security Testing', score: 68, total: 100 },
    { category: 'API Testing', score: 89, total: 100 },
    { category: 'Mobile Testing', score: 71, total: 100 }
  ];

  const skillDistribution = [
    { name: 'Technical Skills', value: 78, color: '#3B82F6' },
    { name: 'Communication', value: 82, color: '#10B981' },
    { name: 'Problem Solving', value: 85, color: '#8B5CF6' },
    { name: 'Domain Knowledge', value: 79, color: '#F59E0B' }
  ];

  const weeklyActivity = [
    { day: 'Mon', mcqs: 12, mockTime: 45 },
    { day: 'Tue', mcqs: 8, mockTime: 30 },
    { day: 'Wed', mcqs: 15, mockTime: 60 },
    { day: 'Thu', mcqs: 10, mockTime: 35 },
    { day: 'Fri', mcqs: 18, mockTime: 75 },
    { day: 'Sat', mcqs: 22, mockTime: 90 },
    { day: 'Sun', mcqs: 6, mockTime: 20 }
  ];

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-700 bg-green-100 border border-green-300';
    if (score >= 70) return 'text-orange-700 bg-orange-100 border border-orange-300';
    return 'text-red-700 bg-red-100 border border-red-300';
  };

  const getReadinessLevel = (score) => {
    if (score >= 90) return { level: 'Excellent', color: 'green-700', bg: 'green-100', description: 'Ready for senior roles' };
    if (score >= 80) return { level: 'Good', color: 'blue-700', bg: 'blue-100', description: 'Ready for mid-level roles' };
    if (score >= 70) return { level: 'Fair', color: 'orange-700', bg: 'orange-100', description: 'More practice needed' };
    return { level: 'Needs Work', color: 'red-700', bg: 'red-100', description: 'Focus on fundamentals' };
  };

  const readiness = getReadinessLevel(userProgress?.readinessScore || 82);

  return (
    <div className="space-y-8">
      {/* Overview Cards - Fixed color classes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <span className="text-2xl">📊</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Overall Readiness</h3>
              <div className={`text-2xl font-bold text-${readiness?.color}`}>
                {userProgress?.readinessScore || 82}%
              </div>
              <p className={`text-sm font-medium text-${readiness?.color} bg-${readiness?.bg} px-2 py-1 rounded inline-block`}>
                {readiness?.level}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <span className="text-2xl">✅</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">MCQs Completed</h3>
              <div className="text-2xl font-bold text-green-700">
                {userProgress?.mcqCompleted || 145}
              </div>
              <p className="text-sm text-green-800 font-medium">87% Accuracy</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <span className="text-2xl">🎥</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Mock Interviews</h3>
              <div className="text-2xl font-bold text-purple-700">
                {userProgress?.mockInterviews || 8}
              </div>
              <p className="text-sm text-purple-800 font-medium">8.5/10 Avg Score</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <span className="text-2xl">🎯</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Study Streak</h3>
              <div className="text-2xl font-bold text-yellow-700">12</div>
              <p className="text-sm text-yellow-800 font-medium">Days Active</p>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Trend Chart - Enhanced styling */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-900">Performance Trends</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line 
                type="monotone" 
                dataKey="readiness" 
                stroke="#3B82F6" 
                strokeWidth={3}
                name="Readiness Score"
              />
              <Line 
                type="monotone" 
                dataKey="mcqScore" 
                stroke="#10B981" 
                strokeWidth={2}
                name="MCQ Score"
              />
              <Line 
                type="monotone" 
                dataKey="mockScore" 
                stroke="#8B5CF6" 
                strokeWidth={2}
                name="Mock Interview Score"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Skills Analysis - Fixed color issues */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Category Breakdown */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h3 className="text-xl font-semibold mb-4 text-gray-900">Category Performance</h3>
          <div className="space-y-4">
            {categoryScores?.map((category, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm text-gray-900">{category?.category}</span>
                  <span className={`px-2 py-1 rounded text-sm font-semibold ${getScoreColor(category?.score)}`}>
                    {category?.score}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${
                      category?.score >= 85 ? 'bg-green-500' :
                      category?.score >= 70 ? 'bg-orange-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${category?.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Skill Distribution - Enhanced legend */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h3 className="text-xl font-semibold mb-4 text-gray-900">Skill Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={skillDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {skillDistribution?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {skillDistribution?.map((skill, index) => (
              <div key={index} className="flex items-center space-x-2 text-sm">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: skill?.color }}
                />
                <span className="text-gray-800 font-medium">{skill?.name}: {skill?.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Weekly Activity - Enhanced styling */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-900">Weekly Activity</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={weeklyActivity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="mcqs" fill="#3B82F6" name="MCQs Completed" />
              <Bar dataKey="mockTime" fill="#10B981" name="Mock Interview (min)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recommendations - Improved text colors */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-900">Personalized Recommendations</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-green-800">Keep Up the Good Work</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span className="text-gray-800">Your testing fundamentals are excellent - continue building on this foundation</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span className="text-gray-800">Great consistency in daily practice - maintain your 12-day streak</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span className="text-gray-800">Mock interview performance is strong - you're ready for interviews</span>
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-orange-800">Areas to Focus</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start space-x-2">
                <span className="text-orange-600 mt-0.5 font-bold">!</span>
                <span className="text-gray-800">Spend more time on Security Testing concepts (68% current score)</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-orange-600 mt-0.5 font-bold">!</span>
                <span className="text-gray-800">Practice more Performance Testing scenarios</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-orange-600 mt-0.5 font-bold">!</span>
                <span className="text-gray-800">Consider additional mock interviews for behavioral questions</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
          <h5 className="font-semibold text-blue-900 mb-2">Next Milestone</h5>
          <p className="text-blue-800 text-sm">
            You're 8% away from achieving "Excellent" readiness level. Focus on Security Testing 
            and Performance Testing to reach 90% overall readiness.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProgressAnalytics;